import React, { useEffect, useRef, useState } from "react";
import { checkboxChecked, emailValid, markError, required, validateEmail } from "../contexts/actions/form/validate";
import FormContactUs from "./FormContactUs";
import FormMessage from "./FormMessage";

window.privacyUrl = window.privacyUrl || '';

const DemandForm = (props) => {
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [filesOnUpload, setFilesOnUpload] = useState([]);

    const contactForm = useRef(null);
    const categoryId = useRef(null);
    const name = useRef(null);
    const city = useRef(null);
    const files = useRef(null);
    const fileLabel = useRef(null);
    const phone = useRef(null);
    const email = useRef(null);
    const message = useRef(null);
    const gdpr = useRef(null);

    useEffect(() => {
        const label = fileLabel.current;
        if (!label) return;
        label.addEventListener('dragenter', handleDragIn);
        label.addEventListener('dragleave', handleDragOut);
        label.addEventListener('dragover', handleDrag);
        label.addEventListener('drop', handleDrop);
        return () => {
            label.removeEventListener('dragenter', handleDragIn);
            label.removeEventListener('dragleave', handleDragOut);
            label.removeEventListener('dragover', handleDrag);
            label.removeEventListener('drop', handleDrop);
        };
    }, []);

    const validFile = () => {
        const el = files.current;
        const hasFile = !!(el?.files && el.files.length > 0);
        if (!hasFile) {
            el?.parentElement?.classList.add('error');
            return false;
        }
        el?.parentElement?.classList.remove('error');
        return true;
    };

    const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDragIn = (e) => { e.preventDefault(); e.stopPropagation(); files.current.parentElement.classList.add('drag'); };
    const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); files.current.parentElement.classList.remove('drag'); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        files.current.parentElement.classList.remove('drag');

        const dropped = e.dataTransfer?.files;
        if (dropped && dropped.length > 0) {
            const dt = new DataTransfer();
            Array.from(dropped).forEach(f => dt.items.add(f));
            files.current.files = dt.files;
            setFilesOnUpload([...dt.files]);
            validFile();
        }
    };
    const handleChangeFileInput = () => {
        setFilesOnUpload([...files.current.files]);
        validFile();
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (window.grecaptcha === undefined) {
            window.location.reload();
        }

        [categoryId, name, email, phone, city, message, files, gdpr].forEach(r => {
            if (r.current) r.current.parentElement.classList.remove('error');
        });

        let valid = true;
        const firstErrorRefs = [];

        if (!required(categoryId)) {
            markError(categoryId);
            valid = false;
            firstErrorRefs.push(categoryId);
        }
        if (!required(name)) {
            markError(name);
            valid = false;
            firstErrorRefs.push(name);
        }
        if (!emailValid(email, validateEmail)) {
            markError(email);
            valid = false;
            firstErrorRefs.push(email);
        }
        if (!required(message)) {
            markError(message);
            valid = false;
            firstErrorRefs.push(message);
        }
        if (!required(phone)) {
            markError(phone);
            valid = false;
            firstErrorRefs.push(phone);
        }
        if (!required(city)) {
            markError(city);
            valid = false;
            firstErrorRefs.push(city);
        }
        if (!checkboxChecked(gdpr)) {
            markError(gdpr);
            valid = false;
            firstErrorRefs.push(gdpr);
        }
        if (!validFile()) {
            markError(files);
            valid = false;
            firstErrorRefs.push(files);
        }

        if (!valid) {
            const first = firstErrorRefs[0];
            if (first) markError(first, { focus: true, scroll: true });
            return;
        }

        if (valid) {
            const csrfToken = document.head.querySelector("[name~=csrf-token][content]").content;
            setSending(true);

            const formData = new FormData(event.target);

            window.grecaptcha.ready(async function () {
                const token = await window.grecaptcha.execute(window.captchaSiteKey || '6LdPEQMpAAAAAFll-gXz9LEZSAvZ558tn4nWQ8c-', { action: 'submit' });
                formData.append("g-recaptcha-response", token);

                const response = await fetch('/api/demand-form', {
                    headers: {
                        "Accept": "application/json",
                        "X-CSRF-Token": csrfToken
                    },
                    'method': 'POST',
                    body: formData
                });

                const data = await response.json();

                // Display error, when server responded with empty response
                if (!data || Object.keys(data).length === 0) {
                    alert("Beim Senden ist ein Fehler aufgetreten");
                    setSending(false);
                    return;
                }

                if (response.status === 200) {
                    const categoryTitle = categoryId.current.options[categoryId.current.selectedIndex].text.trim();

                    window.dataLayer = window.dataLayer || []
                    window.dataLayer.push({
                        'event': 'formSubmission',
                        'formProduct': categoryTitle,
                        'phone': phone.current.value,
                        'email': email.current.value,
                    });

                    contactForm.current.reset();
                    setSent(true);
                    setSending(false);
                    setFilesOnUpload([]);
                } else if (response.status === 500) {
                    alert(response.statusText);
                    setSending(false);
                } else {
                    const errorMessage = Object.values(data.errors)[0][0];
                    setSending(false);
                    alert(errorMessage);
                }
            });
        }
    };

    return (
        <div className="bg-black-full px-4 pt-12 pb-16 text-white lg:pt-16 lg:px-16">
            <div className="relative flex flex-col lg:flex-row lg:gap-12 mx-auto">

                <div className="w-full lg:w-1/2 p-0 md:pr-32">
                    <div className="flex flex-col gap-6">
                        <h2 className="font-28 mb-4">Schreiben Sie uns eine Nachricht</h2>
                        <p className="paragraph-2 text-neutrals-400 font-light">
                            Füllen Sie das Anfrageformular aus und unser Profirol-Experte wird sich bald bei Ihnen melden,
                            um Sie zu beraten und wichtige technische Parameter durchzugehen.
                            Jetzt brauchen wir nur die notwendigsten Informationen, es dauert nur eine Minute.
                        </p>
                    </div>

                    <FormContactUs />
                </div>

                <div className={`w-full lg:w-1/2 mt-10 lg:mt-0 ${sent ? 'hidden' : 'block'}`}>

                    <form
                        ref={contactForm}
                        onSubmit={handleSubmit}
                        className="space-y-6 text-white product-form-placeholder"
                        encType="multipart/form-data"
                        noValidate
                    >
                        <div className="relative floating-label">
                            <select
                                ref={categoryId}
                                className="font-gt-ultra font-light focus:outline-none focus:ring-0 focus:border-neutrals-400"
                                name="category_id"
                                id="category_id"
                                defaultValue=""
                            >
                                <option value="" disabled>Produktkategorie</option>
                                {props.tree.map((category) =>
                                    <optgroup key={category.id} label={category.title} className="font-medium">
                                        {category.sub_categories.map(subCategory =>
                                            <option key={subCategory.id} value={subCategory.id} className="font-light">
                                                {subCategory.title}
                                            </option>
                                        )}
                                    </optgroup>
                                )}
                            </select>
                        </div>

                        <div className="relative floating-label">
                            <input ref={name} type="text" id="name" name="name" placeholder=" " required
                                   className="focus:outline-none focus:ring-0 focus:border-neutrals-400" />
                            <label htmlFor="name" className="text-neutrals-400">Vor- und Nachname*</label>
                        </div>

                        <div className="relative floating-label">
                            <input ref={email} type="email" id="email" name="email" placeholder=" " required
                                   className="focus:outline-none focus:ring-0 focus:border-neutrals-400" />
                            <label htmlFor="email" className="text-neutrals-400">E-Mail*</label>
                        </div>

                        <div className="relative floating-label">
                            <input ref={phone} type="tel" id="phone" name="phone" placeholder=" " required
                                   className="focus:outline-none focus:ring-0 focus:border-neutrals-400" />
                            <label htmlFor="phone" className="text-neutrals-400">Telefon*</label>
                        </div>

                        <div className="relative floating-label">
                            <input ref={city} type="text" id="city" name="city"
                                   placeholder=" " required
                                   className="focus:outline-none focus:ring-0 focus:border-neutrals-400" />
                            <label htmlFor="city" className="text-neutrals-400">Montageort*</label>
                        </div>

                        <div className="relative floating-label">
                            <textarea ref={message} id="message" name="message" rows="5" placeholder=" " required
                                      className="focus:outline-none focus:ring-0 focus:border-neutrals-400"></textarea>
                            <label htmlFor="message" className="text-neutrals-400">Nachricht*</label>
                        </div>

                        <div className="">
                            <label
                                ref={fileLabel}
                                htmlFor="file"
                                className={
                                    "!relative w-full px-4 py-4 border-2 border-dashed border-neutrals-700 rounded flex items-center gap-3 cursor-pointer transition-colors " +
                                    ((filesOnUpload[0] && filesOnUpload[0].name) ? "text-white" : "text-neutrals-400")
                                }
                            >
                                <img src="/images/icons/other/icon_upload.svg" alt="" className="w-5 h-5 shrink-0" />
                                <span className="truncate">
                                    {filesOnUpload[0]?.name || "Bild hochladen"}
                                </span>
                            </label>

                            <input
                                id="file"
                                ref={files}
                                name="file"
                                type="file"
                                className="hidden"
                                onChange={handleChangeFileInput}
                            />
                        </div>

                        <div className="checkbox-container">
                            <label htmlFor="gdpr" className="flex items-start gap-3 cursor-pointer">
                                <input id="gdpr" ref={gdpr} type="checkbox" className="mt-1" />
                                <p className="paragraph-5 text-neutrals-400 max-w-xl">
                                    Mit dem Absenden des Kontaktformulars stimme ich der <a target="_blank" href={window.privacyUrl} className="underline">Verarbeitung personenbezogener Daten</a> durch
                                    Profirol gemäß der Datenschutzrichtlinie zu.
                                </p>
                            </label>
                        </div>

                        <div className="flex justify-end gap-4">
                            <button
                                type="submit"
                                className="form-btn self-start hover:bg-neutrals-500 transition duration-200 hover:text-white"
                                disabled={sending}
                            >
                                {sending ? "Wird gesendet..." : "Absenden"}
                            </button>
                        </div>
                    </form>
                </div>
                {sent ? <FormMessage message="Vielen Dank für Ihre Nachricht, wir werden Sie bald kontaktieren." /> : null}
            </div>
        </div>
    );
};

export default DemandForm;
